import { animate, keyframes, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, OnDestroy, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';
import { AppNotificationDto, LocalUserDto, LocalUserNotificationsApi } from '@buspro/web/apis';
import { LocalAuthService } from '@buspro/web/core';
import { ResourcesLoadingSkeletonsComponent, ResourcesQueryErrorComponent } from '@buspro/web/shared';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzListModule } from 'ng-zorro-antd/list';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { Observable, firstValueFrom, lastValueFrom } from 'rxjs';

@Component({
  selector: 'buspro-admin-notification-center',
  standalone: true,
  imports: [
    CommonModule,
    NzIconModule,
    NzListModule,
    NzButtonModule,
    NzTabsModule,
    MatDividerModule,
    ResourcesLoadingSkeletonsComponent,
    ResourcesQueryErrorComponent,
    NzSpinComponent,
  ],
  template: `
    <section class="app-page px-4 md:px-8 lg:px-12 py-6 md:py-8">
      <!-- Header Section -->
      <div class="mb-8">
        <div class="flex items-center justify-between mb-2">
          <h1 class="text-3xl font-bold text-gray-900">
            <span nz-icon nzType="bell" nzTheme="outline" class="mr-3 text-primary"></span>
            Notification Center
          </h1>
          @if (unreadCount > 0) {
            <span class="px-4 py-2 bg-red-500 text-white rounded-full text-sm font-semibold animate-pulse">
              {{ unreadCount }} New
            </span>
          }
        </div>
        <p class="text-gray-600">Stay updated with your latest notifications and alerts</p>
      </div>

      @switch (notificationsQuery.status()){ @case ('pending') {
      <buspro-resources-loading-skeletons
        [loading]="notificationsQuery.isInitialLoading()"
      ></buspro-resources-loading-skeletons>
      } @case ('error') {
      <buspro-resources-query-error
        [errorMessage]="notificationsQuery!.error()!.message!"
      ></buspro-resources-query-error>
      } @default {
      
      <!-- Actions Bar -->
      <div *ngIf="unreadCount" class="bg-white rounded-xl shadow-sm p-4 mb-6 flex justify-between items-center border border-gray-200">
        <div class="flex items-center">
          <span nz-icon nzType="info-circle" nzTheme="outline" class="text-blue-500 mr-2 text-xl"></span>
          <span class="text-base font-medium text-gray-700">You have {{ unreadCount }} unread notification{{ unreadCount > 1 ? 's' : '' }}</span>
        </div>
        <button nz-button nzType="primary" (click)="markAllAsRead()" class="rounded-lg">
          <span nz-icon nzType="check" nzTheme="outline"></span>
          Mark all as read
        </button>
      </div>

      <nz-spin [nzSpinning]="notificationsQuery.isInitialLoading()">
        <nz-tabset [nzSelectedIndex]="selectedTabIndex()">
          <nz-tab nzTitle="All">
            <nz-list
              *ngIf="notificationsQuery.isSuccess"
              [nzDataSource]="notificationsQuery.data() ?? []"
              [nzRenderItem]="notificationAllItem"
              [nzItemLayout]="'horizontal'"
              [nzNoResult]="emptyTpl"
            >
              <ng-template #notificationAllItem let-item>
                <nz-list-item
                  [@notificationReadState]="item.isRead ? 'read' : 'unread'"
                  class="notification-list-item mb-4 rounded-xl"
                  [ngClass]="{
                    'notification-unread': !item.isRead,
                    'notification-read': item.isRead
                  }"
                >
                  <div class="w-full p-6">
                    <div class="flex items-start justify-between mb-3">
                      <div class="flex items-start flex-1">
                        <div [ngClass]="getIconBackgroundClass(item.type)" 
                             class="rounded-full p-3 mr-4 flex-shrink-0">
                          <span
                            [ngClass]="getIconClass(item.type)"
                            nz-icon
                            [nzType]="getIconType(item.type)"
                            nzTheme="outline"
                            class="text-2xl"
                          ></span>
                        </div>
                        <div class="flex-1">
                          <div class="flex items-center mb-2">
                            <h3 class="text-lg font-bold text-gray-900 mr-3">{{ item.title }}</h3>
                            @if (!item.isRead) {
                              <span class="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                NEW
                              </span>
                            }
                          </div>
                          <p class="text-base text-gray-700 mb-2">{{ item.message }}</p>
                          @if (item.additionalContent) {
                            <p class="text-sm text-gray-600 mt-2">{{ item.additionalContent }}</p>
                          }
                        </div>
                      </div>
                      <div class="text-right ml-4">
                        <div class="text-xs text-gray-500 mb-1">{{ item.createdAt | date : 'short' }}</div>
                        <div class="text-xs text-gray-400">{{ item.createdAt | date : 'mediumDate' }}</div>
                      </div>
                    </div>
                    
                    <div class="flex justify-end items-center gap-2 pt-3 border-t border-gray-200">
                      <button
                        nz-button
                        nzType="default"
                        nzSize="small"
                        (click)="markAsRead(item)"
                        class="rounded-lg"
                      >
                        <span nz-icon [nzType]="item.isRead ? 'eye-invisible' : 'eye'" nzTheme="outline"></span>
                        {{ item.isRead ? 'Mark unread' : 'Mark read' }}
                      </button>
                      <button
                        nz-button
                        nzType="default"
                        nzSize="small"
                        nzDanger
                        (click)="deleteNotification(item)"
                        class="rounded-lg"
                      >
                        <span nz-icon nzType="delete" nzTheme="outline"></span>
                        Delete
                      </button>
                    </div>
                  </div>
                </nz-list-item>
              </ng-template>

              <ng-template #emptyTpl>
                <div class="empty-state p-12 text-center bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl">
                  <div class="mb-6 inline-block p-6 bg-white rounded-full shadow-lg">
                    <span
                      nz-icon
                      nzType="inbox"
                      nzTheme="outline"
                      class="text-7xl text-gray-400"
                    ></span>
                  </div>
                  <h3 class="text-2xl font-bold mb-3 text-gray-900">
                    No notifications yet
                  </h3>
                  <p class="text-gray-600 mb-6 max-w-md mx-auto">
                    Your notification feed is empty. New alerts and updates will appear here when available.
                  </p>
                  <button
                    nz-button
                    nzType="primary"
                    (click)="refreshNotifications()"
                    class="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    <span nz-icon nzType="reload" nzTheme="outline"></span>
                    Check for new notifications
                  </button>
                </div>
              </ng-template>
            </nz-list>
          </nz-tab>
          <nz-tab nzTitle="Unread">
            <nz-list
              [nzDataSource]="unreadNotifications"
              [nzRenderItem]="notificationUnreadItem"
              [nzItemLayout]="'horizontal'"
              [nzNoResult]="unreadEmptyTpl"
            >
              <ng-template #notificationUnreadItem let-item>
                <nz-list-item
                  [@notificationReadState]="item.isRead ? 'read' : 'unread'"
                  class="notification-list-item mb-4 rounded-xl"
                  [ngClass]="{
                    'notification-unread': !item.isRead,
                    'notification-read': item.isRead
                  }"
                >
                  <div class="w-full p-6">
                    <div class="flex items-start justify-between mb-3">
                      <div class="flex items-start flex-1">
                        <div [ngClass]="getIconBackgroundClass(item.type)" 
                             class="rounded-full p-3 mr-4 flex-shrink-0">
                          <span
                            [ngClass]="getIconClass(item.type)"
                            nz-icon
                            [nzType]="getIconType(item.type)"
                            nzTheme="outline"
                            class="text-2xl"
                          ></span>
                        </div>
                        <div class="flex-1">
                          <div class="flex items-center mb-2">
                            <h3 class="text-lg font-bold text-gray-900 mr-3">{{ item.title }}</h3>
                            @if (!item.isRead) {
                              <span class="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                NEW
                              </span>
                            }
                          </div>
                          <p class="text-base text-gray-700 mb-2">{{ item.message }}</p>
                          @if (item.additionalContent) {
                            <p class="text-sm text-gray-600 mt-2">{{ item.additionalContent }}</p>
                          }
                        </div>
                      </div>
                      <div class="text-right ml-4">
                        <div class="text-xs text-gray-500 mb-1">{{ item.createdAt | date : 'short' }}</div>
                        <div class="text-xs text-gray-400">{{ item.createdAt | date : 'mediumDate' }}</div>
                      </div>
                    </div>
                    
                    <div class="flex justify-end items-center gap-2 pt-3 border-t border-gray-200">
                      <button
                        nz-button
                        nzType="default"
                        nzSize="small"
                        (click)="markAsRead(item)"
                        class="rounded-lg"
                      >
                        <span nz-icon [nzType]="item.isRead ? 'eye-invisible' : 'eye'" nzTheme="outline"></span>
                        {{ item.isRead ? 'Mark unread' : 'Mark read' }}
                      </button>
                      <button
                        nz-button
                        nzType="default"
                        nzSize="small"
                        nzDanger
                        (click)="deleteNotification(item)"
                        class="rounded-lg"
                      >
                        <span nz-icon nzType="delete" nzTheme="outline"></span>
                        Delete
                      </button>
                    </div>
                  </div>
                </nz-list-item>
              </ng-template>
              <ng-template #unreadEmptyTpl>
                <div class="empty-state p-12 text-center bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl">
                  <div class="mb-6 inline-block p-6 bg-white rounded-full shadow-lg">
                    <span
                      nz-icon
                      nzType="check-circle"
                      nzTheme="outline"
                      class="text-7xl text-green-500"
                    ></span>
                  </div>
                  <h3 class="text-2xl font-bold mb-3 text-gray-900">All caught up!</h3>
                  <p class="text-gray-600 mb-6 max-w-md mx-auto">
                    You have no unread notifications at the moment. Great job staying on top of things!
                  </p>
                  <button
                    nz-button
                    nzType="primary"
                    (click)="refreshNotifications()"
                    class="rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    <span nz-icon nzType="reload" nzTheme="outline"></span>
                    Check for new notifications
                  </button>
                </div>
              </ng-template>
            </nz-list>
          </nz-tab>
        </nz-tabset>
      </nz-spin>

      } }
    </section>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        min-height: 100vh;
        background: linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%);
      }

      .empty-state {
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .empty-state:hover {
        transform: translateY(-8px) scale(1.02);
      }

      .notification-list-item {
        position: relative;
        overflow: hidden;
        border: 1px solid rgba(226, 232, 240, 0.7);
        border-left-width: 4px;
        transition:
          background 0.45s ease,
          border-color 0.45s ease,
          box-shadow 0.5s ease,
          transform 0.45s cubic-bezier(0.33, 1, 0.68, 1);
      }

      .notification-list-item::after {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        background: radial-gradient(120% 120% at 10% 0%, rgba(59, 130, 246, 0.25) 0%, rgba(59, 130, 246, 0) 70%);
        opacity: 0;
        transition: opacity 0.45s ease, transform 0.45s ease;
      }

      .notification-unread {
        background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
        border-left-color: #2563eb;
        box-shadow: 0 18px 36px rgba(37, 99, 235, 0.18);
      }

      .notification-unread::after {
        opacity: 0.45;
        transform: scale(1);
      }

      .notification-read {
        background: #ffffff;
        border-left-color: #d1d5db;
        box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
      }

      .notification-read::after {
        opacity: 0;
        transform: scale(1.05);
      }

      .notification-list-item:hover {
        transform: translateY(-4px);
        box-shadow: 0 20px 40px rgba(15, 23, 42, 0.14);
      }

      ::ng-deep .ant-tabs-tab {
        font-weight: 600;
        font-size: 16px;
        padding: 12px 24px;
      }

      ::ng-deep .ant-tabs-tab-active {
        color: #1890ff;
      }

      ::ng-deep .ant-spin-container {
        min-height: 400px;
      }
    `,
  ],
  animations: [
    trigger('notificationReadState', [
      transition('unread => read', [
        animate(
          '540ms cubic-bezier(0.22, 1, 0.36, 1)',
          keyframes([
            style({
              boxShadow: '0 24px 48px rgba(37, 99, 235, 0.26)',
              transform: 'translateY(-4px)',
              offset: 0,
            }),
            style({
              boxShadow: '0 16px 32px rgba(59, 130, 246, 0.18)',
              transform: 'translateY(-1px)',
              offset: 0.55,
            }),
            style({
              boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
              transform: 'translateY(0)',
              offset: 1,
            }),
          ]),
        ),
      ]),
      transition('read => unread', [
        animate(
          '380ms cubic-bezier(0.33, 1, 0.68, 1)',
          keyframes([
            style({
              boxShadow: '0 6px 18px rgba(37, 99, 235, 0.14)',
              transform: 'scale(0.97)',
              offset: 0,
            }),
            style({
              boxShadow: '0 20px 40px rgba(37, 99, 235, 0.2)',
              transform: 'scale(1)',
              offset: 1,
            }),
          ]),
        ),
      ]),
    ]),
  ],
})
export class AdminNotificationCenterComponent implements OnDestroy {
  authService = inject(LocalAuthService);
  ngxAuthService = inject(NgxAuthService);
  private usersNotificationsApi = inject(LocalUserNotificationsApi);

  authUser$: Observable<LocalUserDto | null> = this.ngxAuthService.state;
  authUser = toSignal(this.authUser$, { initialValue: null });

  // Default to "All" tab (index 0)
  selectedTabIndex = signal(0);
  private markAllInProgress = false;

  notificationsQuery = injectQuery(() => ({
    queryKey: ['notifications', this.authUser()?.id],
    queryFn: () => this.loadNotifications(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchInterval: false, // Disable polling - use Pusher for real-time updates
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  }));
  private queryClient = injectQueryClient();

  get notifications(): AppNotificationDto[] {
    return this.notificationsQuery.data() ?? [];
  }

  get unreadCount(): number {
    return this.notifications.filter((n) => !n.isRead).length;
  }

  get unreadNotifications(): AppNotificationDto[] {
    return this.notifications.filter((n) => !n.isRead);
  }

  ngOnDestroy() {
    void this.handleMarkAllNotifications(true);
  }

  loadNotifications(): Promise<AppNotificationDto[]> {
    const userId = this.authUser()?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }
    // Use local-user-specific notification endpoint
    return lastValueFrom(
      this.usersNotificationsApi.localUsersNotificationControllerGetUserNotifications({
        id: userId,
      }),
    ).then((result) => result ?? []);
  }

  markAllAsRead() {
    void this.handleMarkAllNotifications(false);
  }

  private async handleMarkAllNotifications(silent: boolean): Promise<void> {
    const userId = this.authUser()?.id;

    if (!userId) {
      return;
    }

    if (this.markAllInProgress) {
      return;
    }

    if (this.unreadNotifications.length === 0) {
      return;
    }

    this.markAllInProgress = true;

    try {
      await firstValueFrom(
        this.usersNotificationsApi.localUsersNotificationControllerMarkAllNotificationAsRead({
          id: userId,
        }),
      );

      await this.queryClient.invalidateQueries({
        queryKey: ['notifications', userId],
      });
    } catch (error) {
      if (silent) {
        console.error('Error auto-marking notifications as read', error);
      } else {
        console.error('Error marking all notifications as read', error);
      }
    } finally {
      this.markAllInProgress = false;
    }
  }

  markAsRead(notification: AppNotificationDto) {
    const userId = this.authUser()?.id;
    if (userId) {
      this.usersNotificationsApi
        .localUsersNotificationControllerMarkNotificationAsRead({
          id: userId,
          notificationId: notification.id,
        })
        .subscribe(
          async () => {
            await this.notificationsQuery.refetch();
          },
          (error) => {
            console.error('Error marking notification as read', error);
          },
        );
    }
  }

  deleteNotification(notification: AppNotificationDto) {
    const userId = this.authUser()?.id;
    if (userId) {
      this.usersNotificationsApi
        .localUsersNotificationControllerDeleteNotification({
          id: userId,
          notificationId: notification.id,
        })
        .subscribe(
          async () => {
            await this.queryClient.invalidateQueries({
              queryKey: ['notifications', userId],
            });
            await this.notificationsQuery.refetch();
          },
          (error) => {
            console.error('Error deleting notification', error);
          },
        );
    }
  }

  async refreshNotifications() {
    const userId = this.authUser()?.id;
    if (userId) {
      await this.queryClient.invalidateQueries({
        queryKey: ['notifications', userId],
      });
    }
  }

  getIconType(type: string): string {
    switch (type) {
      case 'info':
        return 'info-circle';
      case 'success':
        return 'check-circle';
      case 'warning':
        return 'warning';
      case 'error':
        return 'close-circle';
      default:
        return 'bell';
    }
  }

  getIconClass(type: string): string {
    switch (type) {
      case 'info':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  }

  getIconBackgroundClass(type: string): string {
    switch (type) {
      case 'info':
        return 'bg-blue-100';
      case 'success':
        return 'bg-green-100';
      case 'warning':
        return 'bg-yellow-100';
      case 'error':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  }
}
